import React, { useMemo, useState } from 'react';

import { Table, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useAccessControlCatalogQuery } from '@admin/schema';

type LegacyFilter = 'all' | 'legacy' | 'migrated';
type NotesFilter = 'all' | 'with' | 'without';

const ALL = '';

const LEGACY_FILTER_OPTIONS: { value: LegacyFilter; label: string }[] = [
  { value: 'all', label: 'All migration states' },
  { value: 'legacy', label: 'Legacy only' },
  { value: 'migrated', label: 'Migrated only' },
];

const NOTES_FILTER_OPTIONS: { value: NotesFilter; label: string }[] = [
  { value: 'all', label: 'All notes' },
  { value: 'with', label: 'With notes' },
  { value: 'without', label: 'Without notes' },
];

const ENFORCEMENT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: ALL, label: 'All authorization kinds' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'documented', label: 'Documented' },
  { value: 'principal_only', label: 'Principal only' },
  { value: 'pundit', label: 'Pundit' },
  { value: 'roles', label: 'Roles' },
];

const uniqueSorted = (values: string[]) => Array.from(new Set(values)).sort();

const matchesFuzzyId = (id: string, search: string) => {
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = id.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

export const AccessControlCatalogReport: React.FC = () => {
  useTitle('Catalog');
  const [idSearch, setIdSearch] = useState('');
  const [surfaceFilter, setSurfaceFilter] = useState(ALL);
  const [principalFilter, setPrincipalFilter] = useState(ALL);
  const [enforcementFilter, setEnforcementFilter] = useState(ALL);
  const [legacyFilter, setLegacyFilter] = useState<LegacyFilter>('all');
  const [notesFilter, setNotesFilter] = useState<NotesFilter>('all');

  const { data, loading, error } = useAccessControlCatalogQuery({ client });

  const allRows = data?.accessControlCatalog ?? [];

  const filterOptions = useMemo(
    () => ({
      surfaces: uniqueSorted(allRows.map((row) => row.surface)),
      principals: uniqueSorted(allRows.map((row) => row.principal)),
    }),
    [allRows],
  );

  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        if (!matchesFuzzyId(row.id, idSearch)) return false;
        if (surfaceFilter && row.surface !== surfaceFilter) return false;
        if (principalFilter && row.principal !== principalFilter) return false;
        if (enforcementFilter && row.enforcement !== enforcementFilter) return false;
        if (legacyFilter === 'legacy' && !row.legacy) return false;
        if (legacyFilter === 'migrated' && row.legacy) return false;
        if (notesFilter === 'with' && !row.additionalAuthorizationNotes) return false;
        if (notesFilter === 'without' && row.additionalAuthorizationNotes) return false;
        return true;
      }),
    [allRows, idSearch, surfaceFilter, principalFilter, enforcementFilter, legacyFilter, notesFilter],
  );

  const stats = useMemo(() => {
    const legacyCount = rows.filter((row) => row.legacy).length;
    return {
      shown: rows.length,
      total: allRows.length,
      legacy: legacyCount,
      migrated: rows.length - legacyCount,
    };
  }, [rows, allRows.length]);

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Catalog</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Text tag="p" style="muted">
          Each row is one endpoint. The <strong>Authorization</strong> column is the catalog enforcement rule:
          <code>[:pundit, Model, :action?]</code> and <code>:principal_only</code> are checked at the boundary before a
          root field runs; <code>:documented</code> and <code>:legacy</code> are inventory-only (auth lives elsewhere).
          This answers whether someone may invoke an operation at all—not whether they may act on a specific record.
          Optional <strong>Policy scope</strong> on query fields applies <code>Policy::Scope</code> after resolve to
          filter list results—it does not replace boundary authorization.
        </Text>
        <Text tag="p" style="muted">
          Declare GraphQL root fields and REST actions with{' '}
          <code>authorization: {'{ resource: Model, action: :read }'}</code> (catalog id, principal, and surface are
          derived). REST uses <code>access_control_endpoint_for :index, authorization: …</code> on the controller.
          <code>PermissionPolicy</code> subclasses use <code>AccessControl::PermissionChecker::GRANTS</code> until the
          permissions table ships. Use <code>authorization: :documented</code> or <code>:principal_only</code> when
          boundary auth is not Pundit. Use <strong>Notes</strong> for downstream checks (record scope, API keys,
          webhooks, app tokens). Legacy rows come from generated inventory; migrated rows override from declarations.
          See <code>docs/access-control-live-examples.md</code> and{' '}
          <code>docs/adr/0002-endpoint-access-control-catalog.md</code>.
        </Text>

        <div className="row" style={{ marginBottom: 8 }}>
          <div className="col-sm-6">
            <input
              type="search"
              className="form-control"
              placeholder="Search IDs (e.g. graphql.admin mutation)"
              aria-label="Fuzzy catalog id search"
              value={idSearch}
              onChange={(event) => setIdSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="row" style={{ marginBottom: 16 }}>
          <div className="col-sm-2">
            <select
              className="form-control"
              aria-label="Surface filter"
              value={surfaceFilter}
              onChange={(event) => setSurfaceFilter(event.target.value)}
            >
              <option value={ALL}>All surfaces</option>
              {filterOptions.surfaces.map((surface) => (
                <option key={surface} value={surface}>
                  {surface}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <select
              className="form-control"
              aria-label="Principal filter"
              value={principalFilter}
              onChange={(event) => setPrincipalFilter(event.target.value)}
            >
              <option value={ALL}>All principals</option>
              {filterOptions.principals.map((principal) => (
                <option key={principal} value={principal}>
                  {principal}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <select
              className="form-control"
              aria-label="Authorization kind filter"
              value={enforcementFilter}
              onChange={(event) => setEnforcementFilter(event.target.value)}
            >
              {ENFORCEMENT_FILTER_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <select
              className="form-control"
              aria-label="Migration filter"
              value={legacyFilter}
              onChange={(event) => setLegacyFilter(event.target.value as LegacyFilter)}
            >
              {LEGACY_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <select
              className="form-control"
              aria-label="Notes filter"
              value={notesFilter}
              onChange={(event) => setNotesFilter(event.target.value as NotesFilter)}
            >
              {NOTES_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Text tag="p">
          Showing <strong>{stats.shown}</strong> of <strong>{stats.total}</strong> — <strong>{stats.migrated}</strong>{' '}
          migrated, <strong>{stats.legacy}</strong> legacy.
        </Text>

        {error && (
          <Text tag="p" style="danger">
            {error.message}
          </Text>
        )}

        <Table responsive striped>
          <thead>
            <tr>
              <th>ID</th>
              <th>Surface</th>
              <th>Principal</th>
              <th>Authorization</th>
              <th>Policy scope</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <code>{row.id}</code>
                </td>
                <td>{row.surface}</td>
                <td>{row.principal}</td>
                <td>
                  <code>{row.authorization}</code>
                </td>
                <td>{row.punditPolicyScope ? <code>{row.punditPolicyScope}</code> : '—'}</td>
                <td>{row.additionalAuthorizationNotes ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {loading && <Spinner />}
      </Panel.Body>
    </Panel>
  );
};
